<template>
    <div class="keyword-discovery">
        <div class="keyword-discovery__title">Find more keywords!</div>
        <div class="keyword-discovery__body">
            <div class="keyword-discovery__url">
                <label class="keyword-discovery__field">
                    <span class="keyword-discovery__label">Mapped URL</span>
                    <input v-if="preselectedUrl && lockUrlField" 
                        class="keyword-discovery__input keyword-discovery__input_dummy" 
                        type="text" 
                        :value="preselectedUrl.url"
                        :title="preselectedUrl.url"
                        disabled />
                    <base-smart-input-autocomplete v-else
                        easyModel
                        ref="selected-url"
                        class="keyword-discovery__input"
                        :options="autocompleteMappedUrls"
                        v-model="selectedUrl"
                        placeholder="Mapped URL"
                    >
                        <base-material-design-icon class="keyword-discovery__clear-url" 
                            v-if="selectedUrl" 
                            @click.stop="clearSelectedUrl" 
                            icon="close" />
                    </base-smart-input-autocomplete>
                </label>
            </div>    
            <form class="keyword-discovery__keyword"
                @submit.prevent="searchKeywords()">
                <label class="keyword-discovery__field">
                    <span class="keyword-discovery__label">
                        Enter your starter keyword phrase <span class="keyword-discovery__label_thin">(more specific is better!)</span>
                    </span>
                    <base-smart-input-search class="keyword-discovery__input keyword-discovery__input_keyword"
                        easyModel
                        ref="searchInput"
                        :allowFreeText="true"
                        @change="handleKeywordChange($event)"
                        v-model="keyword"
                        placeholder="Enter a keyword"
                    >
                        <base-material-design-icon icon="search" class="keyword-discovery__search-icon" />
                    </base-smart-input-search>
                </label>
                <button class="keyword-discovery__button keyword-discovery__button_submit"
                    type="submit"
                    :disabled="!canSearchKeywords"
                >Find Related Keywords</button>
            </form>

            <div class="keyword-discovery__content">
                <div v-if="!keywordsList"
                    class="keyword-discovery__tutorial">
                    <div class="keyword-discovery__tutorial-item">
                        <base-material-design-icon class="keyword-discovery__tutorial-icon"
                            icon="arrowForward" />
                        <p>Start with a keyword phrase, and find awesome additional terms to strengthen your content</p>
                    </div>
                    <div class="keyword-discovery__tutorial-item">
                        <base-material-design-icon class="keyword-discovery__tutorial-icon"
                            icon="arrowForward" />
                        <p>No Dupes!  Any terms already existing in your project are automatically filtered from our suggestion list</p>
                    </div>
                    <div class="keyword-discovery__tutorial-item">
                        <base-material-design-icon class="keyword-discovery__tutorial-icon"
                            icon="arrowForward" />
                        <p>Keywords will automatically be added to the content brief of the mapped URL selected</p>
                    </div>
                </div>
                <div v-else-if="keywordsList.length"
                    class="keyword-discovery__table-holder">
                    <div class="common-table keyword-discovery__table">
                        <div class="common-table__row common-table__header">
                            <div class="common-table__header-cell keyword-discovery__checkbox-cell">
                                <base-check-box v-model="selectAllCbState" 
                                    :trueValue="1" 
                                    :falseValue="-1" 
                                    :indeterminate="selectAllCbState === 0" 
                                    @change="allKeywordsSelectionChange()"
                                />
                            </div>
                            
                            <div v-for="column in tableConfig" 
                                class="common-table__header-cell"
                                :class="'keyword-discovery__' + column.propName + '-cell'">
                                <base-table-sortable-column class="keyword-discovery__sortable-header"
                                    :label="column.label"
                                    :prop-name="column.propName" 
                                    :sort-config="sortConfig"
                                    @click.native="onHeaderCellClicked(column)" />
                            </div>
                        </div>
                        <div class="keyword-discovery__table-body" :ref="'scrollableTablePart'">
                            <div v-for="keywordItem in sortedKeywordsList"
                                class="common-table__row"
                                :class="{'keyword-discovery__row_selected': keywordItem.selected}">
                                <div class="common-table__cell keyword-discovery__checkbox-cell">
                                    <base-check-box v-model="keywordItem.selected" 
                                        @change="keywordSelectionChange(keywordItem)"/>
                                </div>
                                <div v-for="column in tableConfig" 
                                    class="common-table__cell"
                                    :class="'keyword-discovery__' + column.propName + '-cell'"
                                >
                                    <template v-if="column.propName === 'keyword'">
                                        {{ keywordItem[column.propName] }}
                                    </template>
                                    <template v-else>
                                        {{ keywordItem[column.propName] | commaDividedNumber }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p><strong>{{ keywordsList.length }} Keywords Suggested:</strong> {{ totalSelectedKeywords }} Selected</p>
                </div>
                <div v-else
                    class="keyword-discovery__not-found">
                    <p class="keyword-discovery__not-found-subtitle">0 Results based on starter keyword</p>
                    <p class="keyword-discovery__not-found-subtitle">Enter a new starter keyword and try again</p>
                    <p>Doh! Starter keyword <strong>{{this.trimmedKeyword}}</strong> gave us a false start!</p>
                    <p>Try inputting another phrase and we’ll try and do better, we promise 🤟</p>
                    <ol>
                        <li>Aim for 3-5 word phrases for best suggestions - 2 is too few, more than five is too jive for us often to provide enough suggestions</li>
                        <li>A first round discovery will often lead to a second helpful query - doing a second round with a second starter keyword often leads to powerful second round suggestions!</li>
                        <li>Keyword volume is always based on history - if you have a ‘new new thing’ then past data may not be helpful for predicting future search behavior - follow your marketer’s instincts! </li>
                    </ol>

                </div>
            </div>
            <div v-if="isDataLoading"
                class="keyword-discovery__loader">
                <base-loader/>
            </div>
        </div>

        <div class="keyword-discovery__controls">
            <button
                class="keyword-discovery__button"
                @click="close()"
              >
                Cancel
              </button>
              <button
                class="keyword-discovery__button keyword-discovery__button_submit"
                :disabled="isSubmitButtonDisabled"
                @click="addKeywords()"
              >
                Add Keywords
              </button>
        </div>
    </div>
</template>
<script>
import BaseModalMixins from '@/common/BaseModalMixins'
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions } from 'vuex'
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import ToasterService from '@/services/toasterService'
import { EventBus } from '@/services/eventBus'

const CB_STATES_MAP = {
    checked: 1,
    unchecked: -1,
    indeterminate: 0
}

export default {
    name: 'NewKeywordDiscoveryModal',
    mixins: [BaseModalMixins, BaseSortMixins],
    filters: { ...filters },
    props: {
        /**
         * {
         *  url: string,
         *  urlId: string
         * }
         */
        preselectedUrl: Object,
        lockUrlField: Boolean,
        mappedUrls: Array
    },
    data() {
        return {
            selectedUrl: this.preselectedUrl && this.preselectedUrl.url || '',
            isDataLoading: false,
            keyword: '',
            appliedKeyword: '',
            keywordsList: null,
            tableConfig: [
                {
                    label: 'Keyword',
                    propName: 'keyword'
                },
                {
                    label: 'Search Volume / Mo.',
                    propName: 'msv'
                },
                {
                    label: 'Keyword Difficulty',
                    propName: 'difficultyScore'
                }
            ],
            selectAllCbState: CB_STATES_MAP.unchecked,
            isLoading: false,
            sortConfig: [{
                sortDirection: helper.DIRECTION_DESC,
                propertyName: 'msv'
            }],
            totalSelectedKeywords: 0,
            selectedKeywords: []
        }
    },
    computed: {
        autocompleteMappedUrls() {
            if (!this.mappedUrls || !this.mappedUrls.length) {
                return []
            }
            return this.mappedUrls?.filter(v => v.label.includes(this.selectedUrl))
        },
        selectedUrlId() {
            return this.mappedUrls?.find(v => v.label === this.selectedUrl)?.value
        },
        trimmedKeyword() {
            return this.keyword.trim()
        },
        canSearchKeywords() {
            return !!this.trimmedKeyword.length && this.trimmedKeyword !== this.appliedKeyword
        },
        sortedKeywordsList() {
            if (!this.sortConfig.length) {
                return this.keywordsList
            }

            function sortData(data, param, direction) {
                helper.sortByParamMutation(data, param, direction)
                data.forEach(item => {
                    if (item.children && item.children.length) {
                        sortData(item.children, param, direction)
                    }
                })
            }
            const { propertyName, sortDirection } = this.sortConfig[0]
            const sortedData = _.cloneDeep(this.keywordsList)
            sortData(sortedData, propertyName, sortDirection)

            return sortedData
        },
        isSubmitButtonDisabled() {
            return this.totalSelectedKeywords === 0 || this.isDataLoading
        }
    },
    methods: {
        ...mapActions('keywordCensus', [
            'getKeywordsDiscovery',
            'postKeywordsDiscovery'
        ]),
        clearSelectedUrl() {
            this.selectedUrl = ''
            const input = this.$refs['selected-url']
            if (input) {
                input.clear()
            }
        },
        close() {
            this.$emit('close')
        },
        async searchKeywords() {
            if (!this.canSearchKeywords) {
                return
            }

            this.$refs.searchInput.blur()
            this.isDataLoading = true
            this.appliedKeyword = this.trimmedKeyword
            const { data, errors } = await this.getKeywordsDiscovery({
                clientId: this.clientId,
                keyword: this.trimmedKeyword
            });
            this.isDataLoading = false
            this.totalSelectedKeywords = 0;

            if (this.$refs.scrollableTablePart) {
                this.$refs.scrollableTablePart.scrollTo(0, 0);
            }
            this.selectAllCbState = CB_STATES_MAP.unchecked
            this.$refs.searchInput.focus()
            if (errors.length) {
                console.log(errors);
                return;
            }

            this.keywordsList = data;
        },
        allKeywordsSelectionChange() {
            this.keywordsList.forEach((keyword, index) => {
                const isSelected = this.selectAllCbState === CB_STATES_MAP.checked
                keyword.selected = isSelected
                this.sortedKeywordsList[index].selected = isSelected
            });
            this.totalSelectedKeywords = this.selectAllCbState === CB_STATES_MAP.checked ? this.keywordsList.length : 0;
        },
        keywordSelectionChange(keyword) {
            keyword.selected ? this.totalSelectedKeywords++ : this.totalSelectedKeywords--
            // due to sorting is copying original list, need to update item in original list. Else sorting will drop selection
            const originalKeywordtoChange = this.keywordsList.find(originalKeyword => keyword.keyword === originalKeyword.keyword)
            if (!originalKeywordtoChange) {
                return
            }
            originalKeywordtoChange.selected = keyword.selected;

            if (this.totalSelectedKeywords <= 0) {
                this.selectAllCbState = CB_STATES_MAP.unchecked
            } else if (this.totalSelectedKeywords >= this.keywordsList.length) {
                this.selectAllCbState = CB_STATES_MAP.checked
            } else {
                this.selectAllCbState = CB_STATES_MAP.indeterminate
            }
        },
        handleKeywordChange(data) {
            this.keyword = typeof data === 'string' ? data : data.text
        },
        async addKeywords() {
            const selectedKeywords = this.sortedKeywordsList.filter(keyword => keyword.selected)
                .map(({selected, ...keyword}) => keyword);
                const data = {
                    clientId: this.clientId,
                    urlId: this.preselectedUrl ? this.preselectedUrl.urlId : this.selectedUrlId,
                    keywords: selectedKeywords,
                }
                if (!this.preselectedUrl && !this.selectedUrlId) {
                    data.url = this.selectedUrl
                }
            this.isDataLoading = true
            const response = await this.postKeywordsDiscovery(data)
            this.isDataLoading = false
            if (!response || response.errors && response.errors.length) {
                return
            }

            const isMultiple = selectedKeywords.length > 1;
            const message = `${selectedKeywords.length} new keyword${ isMultiple ? 's' : ''} added!`
            ToasterService.showToast(ToasterService.SUCCESS_TYPE,  message)
            this.close()
            EventBus.$emit('keywords-added')
        }
    },
    mounted() {
        this.$refs.searchInput.focus()   
    }
}
</script>
<style scoped lang="scss">
.keyword-discovery {
    padding: 40px;
    display: flex;
    flex-direction: column;
    max-height: 95vh;
    box-sizing: border-box;

    &__title {
        display: block;
        font-size: 35px;
        font-weight: 500;
        text-align: center;
    }

    &__body {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
        margin: 10px -10px;
        padding: 10px;
    }

    &__loader {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, .1);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__url,
    &__keyword {
        margin-bottom: 20px;
    }

    &__field {
        display: block;
        width: 55%;
        flex: 0 0 55%;
        position: relative;
        padding-top: 20px;

        &:first-child {
            padding-top: 0;
        }
    }

    &__input {
        height: 40px !important;
        box-sizing: border-box;
        width: 100%;

        &_dummy {
            border: 2px solid #e7e9eb;
            border-radius: 4px;
            padding: 0 20px;
            font-weight: 500;
            outline: none !important;
        }

        &_keyword {
            &::v-deep input {
                padding-left: 45px;
            }
        }
    }

    &__label {
        display: block;
        font-weight: 600;
        margin-bottom: 5px;

        &_thin {
            font-weight: 400;
        }
    }

    &__clear-url {
        padding-right: 5px;
    }

    &__search-icon {
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
        color: #c1c3c5;
        pointer-events: none;
    }

    &__keyword {
        display: flex;
        gap: 20px;;
        align-items: flex-end;
    }

    &__content {
        margin-top: 10px;
        flex: 0 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    &__table-holder {
        flex: 0 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    &__table {
        display: flex;
        flex-direction: column;
    }

    &__table-body {
        flex: 0 1;
        overflow-y: auto;
    }

    &__sortable-header {
        background: transparent;
        color: $rfx-dark-march;
        font-size: 14px;
        font-weight: 700;
    }

    .common-table {
        border: 1px solid $rfx-light-steel;

        &__header {
            background-color: rgba(1, 22, 39, .1);
            padding-right: 16px;
        }

        &__header-cell,
        &__cell {
            padding-top: 10px;
            padding-bottom: 10px;
        }
    }

    &__checkbox-cell {
        flex: 0 0 20px;
        padding-left: 20px;
        padding-right: 0;
    }
    
    &__msv-cell {
        flex: 0 0 175px;
    }

    &__difficultyScore-cell {
        flex: 0 0 145px;
    }

    &__row_selected {
        background-color: $rfx-almost-white;
    }

    &__tutorial-item {
        display: flex;
        gap: 10px;
        margin: 0 0 15px;

        p {
            margin: 0;
        }
    }

    &__tutorial-icon {
        font-size: 18px;
        color: $rfx-bright-blue;
    }

    &__not-found {
        p {
            margin: 0 0 15px;
        }

        ol {
            margin: 0;
            padding-left: 20px;
        }

        li {
            margin-bottom: 15px;
        }
    }

    &__not-found-subtitle {
        font-weight: 500;
    }

    &__controls {
        display: flex;
        justify-content: flex-end;
        gap: 20px;;
    }

    &__button {
        height: 40px;
        padding: 0 20px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        border: none;
        outline: none;
        cursor: pointer;
        color: $rfx-dark-march;
        background-color: #fff;

        &_submit {
            background-color: $rfx-medium-march;
            color: #fff;
        }

        &:disabled {
            opacity: .4;
            cursor: default !important;
        }
    }
}
</style>